import { MapPin, Clock, Users, Car, Phone, ArrowRight, Sparkles } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import WhatsAppFloat from '@/components/common/WhatsAppFloat';

import { Button } from '@/components/ui/button';

const PHONE_NUMBER = '919633210606';

const packages = [
  {
    id: 'kannur-sightseeing',
    name: 'Kannur Sightseeing',
    duration: '1 Day',
    distance: '~60 km',
    places: ['St. Angelo Fort', 'Payyambalam Beach', 'Arakkal Museum', 'Dharmadam Island', 'Drive-in Beach', 'Muzhappilangad Beach'],
    highlights: ['Scenic coastal views', 'Historical fort exploration', 'Beach hopping'],
    idealFor: 'Families, Couples, Solo Travelers',
    description: 'Explore the beautiful beaches and historical monuments of Kannur in one day.',
    gradient: 'from-blue-500/10 to-cyan-400/10',
    iconColor: 'text-blue-500'
  },
  {
    id: 'wayanad-tour',
    name: 'Wayanad Tour',
    duration: '2 Days / 1 Night',
    distance: '~280 km',
    places: ['Edakkal Caves', 'Soochipara Falls', 'Banasura Dam', 'Pookode Lake', 'Wayanad Wildlife Sanctuary', 'Chembra Peak'],
    highlights: ['Hill station experience', 'Nature walks', 'Wildlife spotting'],
    idealFor: 'Adventure Seekers, Nature Lovers',
    description: 'Discover the lush green landscapes and adventure spots of Wayanad.',
    gradient: 'from-emerald-500/10 to-green-400/10',
    iconColor: 'text-emerald-500'
  },
  {
    id: 'coorg-trip',
    name: 'Coorg Trip',
    duration: '2 Days / 1 Night',
    distance: '~240 km',
    places: ['Abbey Falls', 'Raja Seat', 'Coffee Plantations', 'Namdroling Monastery', 'Talacauvery', 'Dubare Elephant Camp'],
    highlights: ['Coffee plantation tours', 'Waterfall visits', 'Cultural experience'],
    idealFor: 'Couples, Families',
    description: 'Experience the Scotland of India with breathtaking views and coffee aromas.',
    gradient: 'from-orange-500/10 to-amber-400/10',
    iconColor: 'text-orange-500'
  },
  {
    id: 'kerala-tour',
    name: 'Kerala Tour Package',
    duration: '5 Days / 4 Nights',
    distance: '~800 km',
    places: ['Munnar', 'Thekkady', 'Alleppey', 'Kovalam Beach', 'Athirapally Falls', 'Fort Kochi'],
    highlights: ['Houseboat experience', 'Tea plantations', 'Beach relaxation', 'Wildlife sanctuary'],
    idealFor: 'Honeymooners, Families, Groups',
    description: 'A comprehensive Kerala experience covering hill stations, backwaters, and beaches.',
    gradient: 'from-purple-500/10 to-pink-400/10',
    iconColor: 'text-purple-500'
  },
  {
    id: 'mysore-ooty',
    name: 'Mysore & Ooty Tour',
    duration: '3 Days / 2 Nights',
    distance: '~450 km',
    places: ['Mysore Palace', 'Chamundi Hills', 'Ooty Lake', 'Botanical Gardens', 'Doddabetta Peak', 'Tea Estates'],
    highlights: ['Royal palace visit', 'Toy train ride', 'Mountain scenery'],
    idealFor: 'Families, Senior Citizens',
    description: 'Explore the royal heritage of Mysore and the picturesque hill station of Ooty.',
    gradient: 'from-rose-500/10 to-red-400/10',
    iconColor: 'text-rose-500'
  },
  {
    id: 'airport-transfer',
    name: 'Airport Pickup & Drop',
    duration: 'One Way / Round Trip',
    distance: 'Variable',
    places: ['Kannur Airport', 'Calicut Airport', 'Mangalore Airport'],
    highlights: ['Punctual service', 'Comfortable ride', 'Luggage assistance'],
    idealFor: 'All Travelers',
    description: 'Reliable and comfortable airport transfer services to and from major airports.',
    gradient: 'from-sky-500/10 to-indigo-400/10',
    iconColor: 'text-sky-500'
  },
];

const Packages = () => {
  const handleBookPackage = (packageName: string) => {
    const message = `Hello, I'm interested in the ${packageName} package from Friends On Road Cab, Kannur.`;
    window.open(
      `https://wa.me/${PHONE_NUMBER}?text=${encodeURIComponent(message)}`,
      '_blank'
    );
  };

  const handleCallNow = () => {
    window.location.href = `tel:+${PHONE_NUMBER}`;
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 selection:bg-primary/30">
      {/* Navigation */}
      <Navbar />

      {/* Hero Section */}
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/packages/kerala.png"
            alt="Kerala Tourism"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/70" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-transparent to-slate-950" />
        </div>

        <div className="container relative z-10 px-4 text-center pt-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 backdrop-blur-md border border-slate-700/50 shadow-sm mb-8 animate-fade-up">
            <Sparkles className="w-4 h-4 text-yellow-500" />
            <span className="text-sm font-medium text-gray-300">Premium Travel Experiences</span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 animate-fade-up stagger-1 leading-tight text-white">
            Explore Kerala <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400">
              & Beyond
            </span>
          </h1>

          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-10 animate-fade-up stagger-2 leading-relaxed">
            Curated journeys designed for your comfort. From hill stations to backwaters,
            experience the best of South India with our premium fleet.
          </p>

          <div className="flex flex-wrap justify-center gap-4 animate-fade-up stagger-3">
            <Button
              variant="whatsapp"
              size="lg"
              className="rounded-full px-8 h-14 text-base shadow-lg hover:shadow-xl transition-all duration-300"
              onClick={() => handleBookPackage('General Inquiry')}
            >
              Start Your Journey
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="py-20 relative z-10 bg-slate-950">
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-[-20%] right-[-10%] w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-primary/10 rounded-full blur-[80px] md:blur-[100px]" />
          <div className="absolute bottom-[-20%] left-[-10%] w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-purple-500/10 rounded-full blur-[80px] md:blur-[100px]" />
        </div>

        <div className="container px-4 relative z-10">
          {/* Package Cards with Overlapping Images */}
          <div className="max-w-7xl mx-auto space-y-8 md:space-y-12">
            {packages.map((pkg, index) => (
              <div
                key={pkg.id}
                className="border-slate-800 bg-slate-900/60 backdrop-blur-md shadow-2xl overflow-hidden group hover:border-primary/50 transition-all duration-300 rounded-xl border"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="grid md:grid-cols-2 gap-0">
                  {/* Left Side - Image Section */}
                  <div className="relative h-[300px] md:h-[500px] bg-slate-800/50 overflow-hidden">
                    {pkg.id === 'airport-transfer' ? (
                      /* Single Image Layout for Airport Transfer */
                      <div className="absolute inset-0 p-4">
                        <div className="w-full h-full rounded-lg overflow-hidden shadow-2xl border-4 border-slate-700/50 group-hover:border-sky-500/50 transition-all duration-300 relative z-10">
                          <img
                            src="/images/packages/airport.png"
                            alt={pkg.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
                        </div>
                      </div>
                    ) : (
                      /* Overlapping Collage Layout for Other Packages */
                      <>
                        {/* Main Large Image */}
                        <div className="absolute top-8 left-8 w-[65%] h-[55%] rounded-lg overflow-hidden shadow-2xl z-10 border-4 border-slate-700/50 group-hover:border-primary/50 transition-all duration-300">
                          <img
                            src={`/images/packages/${pkg.id.split('-')[0]}.png`}
                            alt={pkg.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            style={{ objectPosition: 'center top' }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
                        </div>

                        {/* Middle Overlapping Image */}
                        <div className="absolute top-[40%] left-4 w-[45%] h-[35%] rounded-lg overflow-hidden shadow-2xl z-15 border-4 border-slate-700/50 group-hover:border-emerald-500/50 transition-all duration-300">
                          <img
                            src={`/images/packages/${pkg.id.split('-')[0]}-2.png`}
                            alt={`${pkg.name} destination`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            style={{ objectPosition: 'center top' }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
                        </div>

                        {/* Smaller Overlapping Image */}
                        <div className="absolute bottom-8 right-8 w-[55%] h-[45%] rounded-lg overflow-hidden shadow-2xl z-20 border-4 border-slate-700/50 group-hover:border-cyan-500/50 transition-all duration-300">
                          <img
                            src={`/images/packages/${pkg.id.split('-')[0]}-3.png`}
                            alt={`${pkg.name} view`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            style={{ objectPosition: 'center bottom' }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
                        </div>
                      </>
                    )}

                    {/* Decorative Accent Bar */}
                    <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-primary via-cyan-500 to-blue-600" />
                  </div>

                  {/* Right Side - Package Information */}
                  <div className="p-6 md:p-10 flex flex-col justify-center">
                    {/* Package Title */}
                    <div className="mb-6">
                      <div className="inline-block px-3 py-1 rounded-full bg-primary/10 border border-primary/30 mb-3">
                        <span className="text-xs font-semibold text-primary uppercase tracking-wider">Featured Package</span>
                      </div>
                      <h3 className="text-2xl md:text-4xl font-bold text-white mb-3 group-hover:text-primary transition-colors">
                        {pkg.name}
                      </h3>
                      <p className="text-slate-400 text-sm md:text-base leading-relaxed">
                        {pkg.description}
                      </p>
                    </div>

                    {/* Duration & Distance Info */}
                    <div className="grid grid-cols-2 gap-4 mb-6 pb-6 border-b border-slate-700/50">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Clock className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Duration</p>
                          <p className="text-sm font-semibold text-white">{pkg.duration}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">Distance</p>
                          <p className="text-sm font-semibold text-white">{pkg.distance}</p>
                        </div>
                      </div>
                    </div>

                    {/* Highlights */}
                    <div className="mb-6">
                      <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Highlights</h4>
                      <div className="grid grid-cols-1 gap-2">
                        {pkg.highlights.map((highlight, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <div className={`w-1.5 h-1.5 rounded-full bg-current ${pkg.iconColor}`} />
                            <span className="text-sm text-slate-300">{highlight}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Places Covered */}
                    <div className="mb-6">
                      <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Places Covered</h4>
                      <div className="flex flex-wrap gap-2">
                        {pkg.places.map((place, i) => (
                          <span
                            key={i}
                            className="px-3 py-1.5 rounded-full bg-slate-800/80 border border-slate-700/50 text-xs text-slate-300 hover:border-primary/50 hover:text-primary transition-all duration-200"
                          >
                            {place}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Ideal For */}
                    {pkg.idealFor && (
                      <div className="mb-6 pb-6 border-b border-slate-700/50">
                        <h4 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Ideal For</h4>
                        <p className="text-sm text-slate-300">{pkg.idealFor}</p>
                      </div>
                    )}

                    {/* Book Now Button */}
                    <div className="mt-4">
                      <button
                        onClick={() => handleBookPackage(pkg.name)}
                        className="w-full md:w-auto bg-gradient-to-r from-primary to-cyan-600 hover:from-primary/90 hover:to-cyan-600/90 text-white shadow-lg hover:shadow-primary/50 transition-all duration-300 px-8 py-3 rounded-lg group/btn font-semibold flex items-center justify-center gap-2"
                      >
                        Book Now
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container px-4">
          <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 border border-gray-200 shadow-2xl">
            <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-96 h-96 bg-blue-500/20 rounded-full blur-[100px]" />
            <div className="absolute bottom-0 left-0 translate-y-1/2 -translate-x-1/2 w-96 h-96 bg-purple-500/20 rounded-full blur-[100px]" />

            <div className="relative z-10 px-6 py-16 md:px-12 md:py-20 text-center max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                Ready to Design Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Dream Trip?</span>
              </h2>
              <p className="text-lg text-gray-300 mb-10 leading-relaxed max-w-2xl mx-auto">
                Don't see exactly what you're looking for? We specialize in custom itineraries suited to your preferences and schedule.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="whatsapp"
                  size="lg"
                  className="rounded-full h-14 px-8 text-base shadow-lg shadow-green-900/20 hover:shadow-green-900/40"
                  onClick={() => handleBookPackage('Custom Package Inquiry')}
                >
                  Create Custom Package
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="rounded-full h-14 px-8 text-base border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white hover:border-white/40"
                  onClick={handleCallNow}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call Support
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Packages;
